import type { RevenueTrendMonth } from '@servwell/shared';

/**
 * The Revenue chart's geometry (ops insights Phase 2, ticket 05): where each month's bar and
 * year-earlier tick sit, as a percentage of the plot's height, and the clean values the
 * gridlines mark. Pure, so the web suite (node, no DOM) can pin it; `RevenueChart` draws it.
 *
 * ── One scale for both years ────────────────────────────────────────────────
 *
 *   The top of the plot is a clean number at or above the LARGER of both series' largest
 *   month, so a year earlier that was bigger than this year is never drawn off the top, and a
 *   bar and a tick at the same height mean the same amount.
 *
 * ── Nothing below the baseline ──────────────────────────────────────────────
 *
 *   A month can be below zero (a discount-only invoice on Invoiced; refunds outrunning
 *   payments under Collected, ticket 07). A bar cannot draw that: a negative height would draw
 *   nothing or draw upward, and either would misstate it (LESSONS 2026-07-30, the margin bar).
 *   So such a month has no bar and `belowZero` says the chart must state its value in text. A
 *   year earlier below zero has no tick for the same reason, and `yearEarlierBelowZero` says
 *   its figure is stated in text too, in the tick's colour: a month with neither a mark nor a
 *   figure would read as a gap (Rowan's Sep 2025, −50,000, in `revenue-page.test.ts`).
 *
 * A chart where no month is above zero (a shop with nothing in either year) has a top of 0 and
 * one gridline, the baseline, and every mark at 0: never a division by zero.
 */

/** A month as drawn: the server's figures, and where they sit. */
export interface ChartMonth extends RevenueTrendMonth {
  /** The bar's height, in % of the plot. 0 for a month at or below zero. */
  barPct: number;
  /** The year-earlier tick's height, in % of the plot; null (not drawn) below zero. */
  tickPct: number | null;
  /** The month in progress: drawn hatched, "Sep, to the 28th". */
  hatched: boolean;
  /** Below zero: no bar can draw it, so the chart states its value in text. */
  belowZero: boolean;
  /** A year earlier below zero: no tick can draw it, so the chart states it in text. */
  yearEarlierBelowZero: boolean;
}

export interface ChartGeometry {
  months: ChartMonth[];
  /** The value at the top of the plot, in cents; 0 when no month is above zero. */
  topCents: number;
  /** The gridlines, from the baseline up: each one's value in cents and its height in %. */
  gridlines: { cents: number; pct: number }[];
}

/** How many gridline steps the plot aims for above the baseline, at most. */
const STEPS = 4;

/**
 * A clean step for a plot reaching `maxCents`: 1, 2, 2.5 or 5 times a power of ten, the
 * smallest that reaches `maxCents` in `STEPS` steps, and never under a dollar, so an axis
 * never reads "$0.25". Only called with `maxCents` above zero.
 */
function cleanStep(maxCents: number): number {
  const raw = maxCents / STEPS;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * magnitude).find((s) => s >= raw)!;
  return Math.max(100, step);
}

export function chartGeometry(trend: RevenueTrendMonth[]): ChartGeometry {
  const largest = Math.max(0, ...trend.flatMap((m) => [m.cents, m.yearEarlierCents]));
  const step = largest > 0 ? cleanStep(largest) : 0;
  const topCents = step > 0 ? Math.ceil(largest / step) * step : 0;
  const pctOf = (cents: number) => (topCents > 0 ? (cents / topCents) * 100 : 0);

  const gridlines = [{ cents: 0, pct: 0 }];
  for (let cents = step; step > 0 && cents <= topCents; cents += step) {
    gridlines.push({ cents, pct: pctOf(cents) });
  }

  return {
    months: trend.map((m) => ({
      ...m,
      barPct: pctOf(Math.max(0, m.cents)),
      tickPct: m.yearEarlierCents < 0 ? null : pctOf(m.yearEarlierCents),
      hatched: m.partialThrough !== null,
      belowZero: m.cents < 0,
      yearEarlierBelowZero: m.yearEarlierCents < 0,
    })),
    topCents,
    gridlines,
  };
}
